/**
 * `window.MDBCheckout.installmentsEligibility()` - the ONE answer to "may this
 * visitor be shown the landing page's installments block?".
 *
 * WHY THIS EXISTS (2026-09-06). The LP block used to reveal itself only when
 * Stripe's PaymentMethodMessagingElement reported a rendered line. On Live it
 * never revealed: in a harness driving the live AND the test publishable key,
 * with countryCode forced to "US", USD 19900 / 4999, and Klarna / Afterpay /
 * Affirm alone and together, the element always fired `ready` and stayed a 9 px
 * empty iframe. Whether it paints in a real US browser is unproven - so the
 * reveal must not depend on it. Visibility is now decided here, by geo alone,
 * and the provider element is mounted best-effort inside an already-revealed
 * block (see installments-messaging-mount).
 *
 * WHY ONLY "US". Through THIS Stripe account (a US platform account) Klarna and
 * Afterpay are domestic-only - a buyer's Klarna/Afterpay eligibility follows the
 * merchant country, so a DE or GB visitor cannot pay in instalments here even
 * though those providers exist in their market. PayPal Pay Later, the other
 * route into the same control, is US-only for us as well. So a non-US visitor
 * shown "split it into 4 interest-free payments" would reach a checkout that
 * cannot keep the promise. When the account gains a non-US Klarna/Afterpay
 * entity, add its country here - this Set is the single place that decides.
 *
 * WHY A FRESH LOOKUP. `fetchCurrencyData()` returns the sessionStorage-cached
 * `_currency_data` from earlier in the session. The owner tested on a US VPN in
 * a tab that still held "DE" and saw nothing. The country is therefore read
 * from a FRESH same-origin call to /api/geo/currency, done exactly once per page
 * and shared by every card. Only when that call fails do we fall back to the
 * cache, and with neither we return ineligible - an unknown visitor is never
 * shown the promise.
 *
 * Nothing here is a money surface: no amount is read, computed or displayed.
 */

/** Countries whose buyers can actually complete an instalment plan through this
 *  Stripe account (see the header comment). */
export const INSTALLMENTS_ELIGIBLE_COUNTRIES = new Set(["US"]);

export type InstallmentsEligibility = { country: string; eligible: boolean };

/** Session cache written by lib/currency `fetchCurrencyData`. Read ONLY as a
 *  fallback when the fresh lookup fails - never as the primary source. */
function cachedCountry(): string {
  try {
    const raw = window.sessionStorage.getItem("_currency_data");
    if (!raw) return "";
    const data = JSON.parse(raw) as { country?: string };
    return (data?.country || "").trim().toUpperCase();
  } catch {
    return "";
  }
}

async function resolveCountry(): Promise<string> {
  try {
    // Same-origin proxy: it forwards the visitor's real country header to Go.
    // `no-store` so an intermediate cache cannot hand back another visitor's
    // country the way the session cache handed back the stale one.
    const res = await fetch("/api/geo/currency", { cache: "no-store" });
    if (!res.ok) throw new Error("geo lookup failed");
    const json = (await res.json()) as { data?: { country?: string }; country?: string };
    const country = ((json?.data?.country ?? json?.country) || "").trim().toUpperCase();
    if (!country) throw new Error("geo lookup returned no country");
    return country;
  } catch {
    return cachedCountry();
  }
}

let pending: Promise<InstallmentsEligibility> | null = null;

/** Resolved once per page and shared by every installments block on it. */
export function installmentsEligibility(): Promise<InstallmentsEligibility> {
  if (pending) return pending;
  pending = resolveCountry()
    .then((country) => ({
      country,
      eligible: INSTALLMENTS_ELIGIBLE_COUNTRIES.has(country),
    }))
    .catch(() => ({ country: "", eligible: false }));
  return pending;
}

/** Tests only: drops the per-page memoisation. */
export function __resetInstallmentsEligibility() {
  pending = null;
}
